import { Link, useLocation } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import type { ProfileStore } from '../stores/profileStore'
import { gamificationStore } from '../stores/gamificationStore'
import LangSwitcher from './ui/LangSwitcher'
import { cn } from '../lib/cn'

interface HeaderProps {
  profile: ProfileStore
}

const Header = observer(({ profile }: HeaderProps) => {
  const location = useLocation()
  const { t } = useTranslation()
  const xpPercent = Math.round(gamificationStore.xpProgress * 100)

  return (
    <header className="sticky top-0 z-40 safe-area-top">
      <div
        className="border-b border-slate-200/60 shadow-sm backdrop-blur-xl bg-white/90"
      >
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2.5 group shrink-0">
              <div className="relative">
                <img
                  src="/logo.png"
                  alt={t('brand.planetMoji')}
                  className="w-9 h-9 rounded-xl group-hover:scale-105 transition-transform shadow-sm border border-gray-100"
                />
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-300 border-2 border-white" />
              </div>
              <span className="font-title text-lg font-extrabold text-gray-800 hidden sm:inline">
                Planet<span className="text-transparent bg-clip-text bg-[linear-gradient(135deg,#8b9cc8_0%,#b8a0c0_100%)]">Moji</span>
              </span>
            </Link>

            <nav className="flex items-center gap-1 bg-slate-50/80 rounded-2xl p-1 border border-slate-200/60">
              <Link
                to="/"
                className={cn(
                  'px-3 py-1.5 rounded-xl text-sm font-semibold transition-all',
                  location.pathname === '/'
                    ? 'bg-white text-violet-700 shadow-sm border border-violet-100'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/70'
                )}
                aria-label={t('nav.games')}
              >
                🎮
              </Link>
              <Link
                to="/mojiland"
                className={cn(
                  'px-3 py-1.5 rounded-xl text-sm font-semibold transition-all',
                  location.pathname === '/mojiland'
                    ? 'bg-white text-teal-700 shadow-sm border border-teal-100'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/70'
                )}
                aria-label={t('brand.mojiland')}
              >
                🏝️
              </Link>
            </nav>

            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <div className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-xl bg-amber-50/80 border border-amber-100">
                <span className="text-sm">🔥</span>
                <span className="font-semibold text-amber-700/90 text-xs">{profile.scoreForm}</span>
              </div>
              <div className="flex flex-col items-end gap-0.5 min-w-[52px]">
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-xl bg-violet-50/80 border border-violet-100">
                  <span className="text-xs">⭐</span>
                  <span className="font-semibold text-violet-700/90 text-xs">{gamificationStore.level}</span>
                </div>
                <div className="w-full h-1 rounded-full bg-amber-50 overflow-hidden border border-amber-100/60">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${xpPercent}%`,
                      background: 'linear-gradient(90deg, #e8d9a8 0%, #dcc99a 100%)',
                    }}
                  />
                </div>
              </div>
              <LangSwitcher />
              <Link
                to="/profile"
                className="relative flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-xl border-2 border-white shadow-sm hover:ring-2 hover:ring-violet-200 transition-all overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #c8b8e0 0%, #d4b8c8 100%)' }}
              >
                <span className="drop-shadow-sm">{profile.emoji}</span>
                <span className="absolute -bottom-0.5 -right-0.5 text-sm">{profile.flag}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>
  )
})

export default Header
